export interface Scale {
  /** Where the axis ends: a round number at or above the largest value. */
  max: number;
  /** The gridlines, zero included, in rising order. */
  ticks: number[];
}

const STEPS = [1, 2, 2.5, 5, 10];

/**
 * An axis from zero to a round number, with about `count` gridlines. A scale
 * of whole things (people, sessions) never has a gridline at half of one.
 */
export function niceScale(largest: number, count = 4, wholeNumbers = false): Scale {
  if (!(largest > 0)) {
    return { max: 1, ticks: [0, 1] };
  }

  const rough = largest / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const candidates = STEPS.map((s) => s * magnitude).filter((s) => !wholeNumbers || Number.isInteger(s));
  const step = candidates.find((s) => s >= rough) ?? (wholeNumbers ? Math.ceil(rough) : 10 * magnitude);

  const steps = Math.max(1, Math.ceil(largest / step - 1e-9));
  const ticks = Array.from({ length: steps + 1 }, (_, i) => roundTo(i * step, step));
  return { max: ticks[ticks.length - 1], ticks };
}

/** Rids a multiple of a step of floating-point dust: 0.30000000000000004. */
function roundTo(value: number, step: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  return Number(value.toFixed(decimals));
}

/**
 * Which labels to show under the columns so that none runs into the next:
 * every one, every second, every fifth... always counting from the first.
 */
export function labelEvery(steps: number, width: number, labelWidth = 52): number {
  if (steps <= 0 || width <= 0) return 1;
  const fits = Math.max(1, Math.floor(width / labelWidth));
  return Math.max(1, Math.ceil(steps / fits));
}
